import { useState, useEffect } from 'react';
import { X, Share, PlusSquare, ArrowDown } from 'lucide-react';
import { Button } from './ui/button';

const isIOS = () => {
  const ua = navigator.userAgent;
  return /iPad|iPhone|iPod/.test(ua) || (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);
};

const isSafari = () => {
  const ua = navigator.userAgent;
  return /Safari/.test(ua) && !/Chrome|CriOS|FxiOS/.test(ua);
};

const isStandalone = () => {
  return window.matchMedia('(display-mode: standalone)').matches || window.navigator.standalone === true;
};

export const IOSInstallGuide = () => {
  const [show, setShow] = useState(false);

  useEffect(() => {
    if (!isIOS() || !isSafari() || isStandalone()) return;

    const dismissed = localStorage.getItem('ios-install-dismissed');
    if (dismissed && Date.now() - parseInt(dismissed) < 30 * 24 * 60 * 60 * 1000) return;

    // Delay showing to avoid overwhelming users on first visit
    const timer = setTimeout(() => setShow(true), 4000);
    return () => clearTimeout(timer);
  }, []);

  const dismiss = () => {
    setShow(false);
    localStorage.setItem('ios-install-dismissed', Date.now().toString());
  };

  if (!show) return null;

  return (
    <div
      className="fixed bottom-6 left-3 right-3 z-[60] md:hidden animate-in slide-in-from-bottom-6 duration-500"
      data-testid="ios-install-guide"
    >
      <div className="bg-white rounded-2xl shadow-2xl border border-gray-100 overflow-hidden">
        {/* Header */}
        <div className="bg-gradient-to-r from-ocean to-ocean-light px-4 py-3 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <img src="/logo.jpg" alt="Triple A" className="w-8 h-8 rounded-lg" />
            <div>
              <p className="text-white font-semibold text-sm">Install Triple A Travel</p>
              <p className="text-white/70 text-[10px]">Add to your Home Screen</p>
            </div>
          </div>
          <button onClick={dismiss} className="text-white/60 hover:text-white p-1" aria-label="Close">
            <X className="w-4 h-4" />
          </button>
        </div>

        {/* Steps */}
        <div className="px-4 py-3 space-y-3">
          <div className="flex items-start gap-3">
            <div className="w-7 h-7 rounded-full bg-ocean/10 flex items-center justify-center flex-shrink-0 mt-0.5">
              <span className="text-ocean font-bold text-xs">1</span>
            </div>
            <div className="flex-1">
              <p className="text-sm text-gray-700 font-medium">
                Tap the <Share className="w-4 h-4 inline text-ocean -mt-0.5 mx-0.5" /> Share button
              </p>
              <p className="text-xs text-gray-400 mt-0.5">at the bottom of your Safari browser</p>
            </div>
          </div>

          <div className="flex items-start gap-3">
            <div className="w-7 h-7 rounded-full bg-ocean/10 flex items-center justify-center flex-shrink-0 mt-0.5">
              <span className="text-ocean font-bold text-xs">2</span>
            </div>
            <div className="flex-1">
              <p className="text-sm text-gray-700 font-medium">
                Scroll and tap <PlusSquare className="w-4 h-4 inline text-ocean -mt-0.5 mx-0.5" /> Add to Home Screen
              </p>
            </div>
          </div>

          <div className="flex items-start gap-3">
            <div className="w-7 h-7 rounded-full bg-ocean/10 flex items-center justify-center flex-shrink-0 mt-0.5">
              <span className="text-ocean font-bold text-xs">3</span>
            </div>
            <div className="flex-1">
              <p className="text-sm text-gray-700 font-medium">
                Tap <span className="text-ocean font-semibold">Add</span> to install
              </p>
            </div>
          </div>
        </div>

        {/* Bottom action */}
        <div className="px-4 pb-3 flex items-center justify-between">
          <Button
            variant="ghost"
            size="sm"
            onClick={dismiss}
            className="text-gray-400 hover:text-gray-600 text-xs h-8"
          >
            Maybe later
          </Button>
          <div className="flex items-center gap-1 text-ocean text-xs font-medium">
            <ArrowDown className="w-3.5 h-3.5 animate-bounce" />
            <span>Look for the share icon below</span>
          </div>
        </div>
      </div>
    </div>
  );
};
